/**
 * External Browser Service
 * 
 * Handles opening external URLs in the device's native browser
 * instead of inside the app (WebView), which prevents security
 * and UX issues in Capacitor apps.
 */

import { Injectable } from '@angular/core';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';

@Injectable({
  providedIn: 'root',
})
export class ExternalBrowserService {
  /**
   * Opens a URL in the device's external browser (Safari/Chrome)
   * Falls back to window.open for web platform
   * 
   * @param url - The URL to open
   * @param options - Optional configuration
   */
  async openUrl(url: string, options?: { windowName?: string }): Promise<void> {
    if (!url) {
      console.warn('ExternalBrowserService: No URL provided');
      return;
    }

    try {
      if (Capacitor.isNativePlatform()) {
        // On native platforms, use Capacitor Browser plugin
        // This opens the URL in the device's default browser (Safari/Chrome)
        await Browser.open({
          url,
          presentationStyle: 'popover', // iOS: opens in a sheet
        });
        console.log('🌐 Opened URL in external browser:', url);
      } else {
        // On web platform, use window.open
        window.open(url, options?.windowName || '_blank', 'noopener,noreferrer');
        console.log('🌐 Opened URL in new tab:', url);
      }
    } catch (error) {
      console.error('Error opening external URL:', error);
      // Fallback to window.open
      window.open(url, options?.windowName || '_blank', 'noopener,noreferrer');
    }
  }

  /**
   * Opens a legal document URL (convenience method)
   * @param documentType - The type of legal document
   */
  async openLegalDocument(documentType: 'privacy-policy' | 'terms-service' | 'cookie-policy' | 'gdpr-rights'): Promise<void> {
    const baseUrl = 'https://gastronauta.app/landing/legal';
    const documentPaths: Record<string, string> = {
      'privacy-policy': 'politica-de-privacidad',
      'terms-service': 'terminos-servicio',
      'cookie-policy': 'politica-cookies',
      'gdpr-rights': 'derechos-gdpr',
    };

    const path = documentPaths[documentType];
    if (path) {
      await this.openUrl(`${baseUrl}/${path}`);
    } else {
      console.warn('Unknown legal document type:', documentType);
    }
  }

  /**
   * Opens a social media URL
   * @param url - The social media URL
   */
  async openSocialMedia(url: string): Promise<void> {
    await this.openUrl(url);
  }

  /**
   * Opens a maps URL (Google Maps, Apple Maps, etc.)
   * @param url - The maps URL
   */
  async openMaps(url: string): Promise<void> {
    await this.openUrl(url);
  }

  /**
   * Opens a phone dialer with a number
   * @param phoneNumber - The phone number to dial
   */
  async openPhone(phoneNumber: string): Promise<void> {
    const telUrl = `tel:${phoneNumber.replace(/\s/g, '')}`;
    await this.openUrl(telUrl);
  }

  /**
   * Opens an email client with a pre-filled address
   * @param email - The email address
   * @param subject - Optional email subject
   */
  async openEmail(email: string, subject?: string): Promise<void> {
    let mailtoUrl = `mailto:${email}`;
    if (subject) {
      mailtoUrl += `?subject=${encodeURIComponent(subject)}`;
    }
    await this.openUrl(mailtoUrl);
  }
}
